import { Flex, Text, Link, Button, View } from '@smarterservices/smarter-ui';
import DualPane from '../../DualPane';
import StepInstructions from '../../Common/StepInstructions';
import { useAppStore } from '../../../stores/app';
import { useState, useEffect } from 'react';
import Loader from '../../Common/Loader';
import ManualLink from './ManualLink';
import QRCodeConnection from './QRCodeConnection';
import CameraConnected from './CameraConnected';
import ParticipantConnected from './ParticipantConnected';
import config from '../../../config';

/**
 * SecondCameraConnection Component
 * 
 * Main orchestrator component for managing second camera device connection in proctoring sessions.
 * Handles the complete flow from device linking to camera connection, including QR code generation,
 * manual code entry, participant connection states, and active camera management.
 * 
 * The component manages four distinct states:
 * - 'qrCode': Initial state showing QR code for device connection
 * - 'manualScan': Fallback state showing manual code entry
 * - 'participantConnected': Intermediate state when participant joins but video isn't ready
 * - 'cameraView': Final state with active camera controls and video preview
 * 
 * @param {Object} props - Component props
 * @param {Object} props.stepData - Step configuration containing instructions and texts for different states
 * @param {string} props.stepData.qrCodeLinkInstructions - Instructions for QR code scanning
 * @param {string} props.stepData.manualLinkInstructions - Instructions for manual code entry
 * @param {string} props.stepData.cameraConnectedInstructions - Instructions for connected camera
 * @param {Function} props.setIsTaskComplete - Callback to mark the connection task as complete
 * @returns {React.ReactElement} Dynamic interface based on current connection state
 */
const SecondCameraConnection = ({ stepData, setIsTaskComplete }) => {
  // Local state management
  const [deviceLinkCode, setDeviceLinkCode] = useState(''); // Generated code for device linking
  const [rightPaneContent, setRightPaneContent] = useState('qrCode'); // Current UI state
  const [isVideoStreaming, setIsVideoStreaming] = useState(false); // Video stream status

  // App store data and functions
  const { apiClient, setIsSecondCameraConnected } = useAppStore();

  // Store selectors for connection state
  const initialized = useAppStore((state) => state.initialized);
  const isSecondCameraConnected = useAppStore((state) => state.isSecondCameraConnected);
  const secondCameraParticipant = useAppStore((state) => state.secondCameraParticipant);
  const secondCameraPublication = useAppStore((state) => state.secondCameraPublication);

  /**
   * Effect to monitor video streaming status
   * Updates local state when video publication changes
   */
  useEffect(() => {
    if (secondCameraPublication) {
      setIsVideoStreaming(
        secondCameraPublication?.isSubscribed &&
        secondCameraPublication?.track &&
        secondCameraPublication?.track?.mediaStream?.active || false
      );
    }
  }, [secondCameraPublication]);

  /**
   * Generates and stashes handoff data for device linking
   * Creates a temporary code that the second camera device can use to connect
   * Stores session data with TTL for secure device authentication
   */
  const stashHandoffData = async () => {
    const stashData = await apiClient.stash({
      ttl: config.session.secondCamera.deviceLinkCodeExpiration,
      data: {
        sessionSid: useAppStore.getState().sessionSid,
        installSid: useAppStore.getState().installSid,
        examSid: useAppStore.getState().examSid,
        token: useAppStore.getState().token,
      },
      code: {
        length: config.session.secondCamera.codeLength,
        type: 'alpha',
      },
    });
    setDeviceLinkCode(stashData.code.toUpperCase());
  };

  /**
   * Main effect to manage UI state transitions based on connection status
   * Determines which interface to show based on participant and publication state
   */
  useEffect(() => {
    if (!initialized) {
      console.log('SecondCameraConnection: Not initialized');
      return;
    }

    // Check if video publication is ready for use
    const isPublicationReady =
      secondCameraPublication?.isSubscribed &&
      secondCameraPublication?.track &&
      secondCameraPublication?.track?.mediaStream?.active || false;

    // State transition logic
    if (secondCameraParticipant && isPublicationReady) {
      // Participant connected with active video - show camera controls
      setRightPaneContent('cameraView');
    } else if (secondCameraParticipant && !isPublicationReady) {
      // Participant connected but video not ready - show waiting state
      setRightPaneContent('participantConnected');
    } else {
      // No participant - show connection interface
      if (!deviceLinkCode) {
        stashHandoffData();
      }
      setRightPaneContent('qrCode');
    }
  }, [
    secondCameraParticipant,
    secondCameraPublication,
    isSecondCameraConnected,
    isVideoStreaming,
    initialized
  ]);

  // Text replacements for dynamic content in instructions
  const replacements = {
    mobileDeviceUrl: config.proctoring.ui.secondCamera.shortDeviceBaseUrl,
  };


  // Render appropriate interface based on current state
  if (rightPaneContent === 'cameraView') {
    // Active camera connection with controls and video preview
    return (
      <DualPane
        leftPane={
          <StepInstructions
            instructions={stepData?.cameraConnectedInstructions}
            replacements={replacements}
          />
        }
        rightPane={
          <Flex direction="column" padding="medium" alignItems="center">
            <CameraConnected
              stepData={stepData}
              setRightPaneContent={setRightPaneContent}
            />
          </Flex>
        }
      />
    );
  } else if (rightPaneContent === 'participantConnected') {
    // Participant joined but video not ready - show platform-specific instructions
    return (
      <ParticipantConnected stepData={stepData} replacements={replacements} />
    );
  } else if (rightPaneContent === 'manualScan') {
    // Manual code entry fallback when QR scanning fails
    return (
      <DualPane
        leftPane={
          <StepInstructions
            instructions={stepData?.manualLinkInstructions}
            replacements={replacements}
          />
        }
        rightPane={
          <Flex direction="column" padding="medium" alignItems="center">
            <ManualLink
              stepData={stepData}
              deviceLinkCode={deviceLinkCode}
              setRightPaneContent={setRightPaneContent}
            />
          </Flex>
        }
      />
    );
  } else {
    // Default state - QR code connection interface
    return (
      <DualPane
        leftPane={
          <StepInstructions
            instructions={stepData?.qrCodeLinkInstructions}
            replacements={replacements}
          />
        }
        rightPane={
          <Flex direction="column" padding="medium" alignItems="center">
            <QRCodeConnection
              stepData={stepData}
              deviceLinkCode={deviceLinkCode}
              setRightPaneContent={setRightPaneContent}
            />
          </Flex>
        }
      />
    );
  }
};

export default SecondCameraConnection;
